import { useMemo } from "react"
import formatPhone from "@/utils/format-phone"

import { useFetchCurrentClientIntegrations } from "@/hooks/api/current-client-integrations"
import { useFetchDialpadIntegrations } from "@/hooks/api/dialpad-integrations"
import { useFetchGoToIntegrations } from "@/hooks/api/go-to-integrations"
import { useFetchIntulseIntegrations } from "@/hooks/api/intulse-integrations"
import { useFetchMyRepChatIntegrations } from "@/hooks/api/my-rep-chat-integrations"
import { useFetchRingCentralIntegrations } from "@/hooks/api/ring-central-integrations"
import { useFetchZoomPhoneIntegrations } from "@/hooks/api/zoom-phone-integrations"

export interface PhoneIntegrationOption {
  value: string
  label: string
  detail?: string
}

interface UsePhoneIntegrationsOptions {
  enabled?: boolean
}

export function usePhoneIntegrations({ enabled = true }: UsePhoneIntegrationsOptions = {}) {
  const { data: currentClientIntegrations, isFetching: isFetchingCurrentClient } =
    useFetchCurrentClientIntegrations(enabled)
  const { data: ringCentralIntegrations, isFetching: isFetchingRingCentral } = useFetchRingCentralIntegrations(enabled)
  const { data: myRepChatIntegrations, isFetching: isFetchingMyRepChat } = useFetchMyRepChatIntegrations(enabled)
  const { data: dialpadIntegrations, isFetching: isFetchingDialpad } = useFetchDialpadIntegrations(enabled)
  const { data: intulseIntegrations, isFetching: isFetchingIntulse } = useFetchIntulseIntegrations(enabled)
  const { data: goToIntegrations, isFetching: isFetchingGoTo } = useFetchGoToIntegrations(enabled)
  const { data: zoomPhoneIntegrations, isFetching: isFetchingZoomPhone } = useFetchZoomPhoneIntegrations(enabled)

  const isLoading =
    isFetchingCurrentClient ||
    isFetchingRingCentral ||
    isFetchingMyRepChat ||
    isFetchingDialpad ||
    isFetchingIntulse ||
    isFetchingGoTo ||
    isFetchingZoomPhone

  const options = useMemo<PhoneIntegrationOption[]>(() => {
    const result: PhoneIntegrationOption[] = []

    currentClientIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const inboxes = integration.inboxes ?? []
      inboxes.forEach((phone) => {
        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label: integration.account_name || "CurrentClient",
          detail: formatPhone(phone),
        })
      })
    })

    ringCentralIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const smsPhones = integration.sms_capable_phone_numbers ?? []
      const label = integration.user?.full_name || integration.extension_name || "RingCentral"

      smsPhones.forEach((phone) => {
        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label,
          detail: formatPhone(phone),
        })
      })
    })

    myRepChatIntegrations?.forEach((integration) => {
      if (!integration.phone_number || !integration.connected) return

      result.push({
        value: `${integration.type}|${integration.guid}|${integration.phone_number}`,
        label: "MyRepChat",
        detail: formatPhone(integration.phone_number),
      })
    })

    // The office/main line is shared across every Dialpad advisor on the book, so track which
    // ones we've already offered to avoid listing the same main line once per integration.
    const seenDialpadOfficeNumbers = new Set<string>()
    dialpadIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const label = integration.account_name || integration.email || "Dialpad"

      const phones = integration.phone_numbers ?? []
      phones.forEach((phone) => {
        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label,
          detail: formatPhone(phone),
        })
      })

      const officePhones = integration.office_phone_numbers ?? []
      officePhones.forEach((phone) => {
        if (seenDialpadOfficeNumbers.has(phone)) return
        seenDialpadOfficeNumbers.add(phone)

        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label: "Main line",
          detail: formatPhone(phone),
        })
      })
    })

    // Verified sender DIDs, not the short-extension phone_numbers list.
    const seenIntulseNumbers = new Set<string>()
    intulseIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const label = integration.account_name || integration.email || "Intulse"

      const phones = integration.sendable_phone_numbers ?? []
      phones.forEach((phone) => {
        if (seenIntulseNumbers.has(phone)) return
        seenIntulseNumbers.add(phone)

        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label,
          detail: formatPhone(phone),
        })
      })
    })

    goToIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const label = integration.account_name || integration.line_name || "GoTo Connect"
      integration.phone_numbers?.forEach((phone) => {
        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label,
          detail: formatPhone(phone),
        })
      })
    })

    zoomPhoneIntegrations?.forEach((integration) => {
      if (!integration.configured || !integration.connected) return

      const phones = integration.phone_numbers ?? []
      const label = integration.display_name || integration.account_email || "Zoom Phone"

      phones.forEach((phone) => {
        result.push({
          value: `${integration.type}|${integration.guid}|${phone}`,
          label,
          detail: formatPhone(phone),
        })
      })
    })

    return result
  }, [
    currentClientIntegrations,
    ringCentralIntegrations,
    myRepChatIntegrations,
    dialpadIntegrations,
    intulseIntegrations,
    goToIntegrations,
    zoomPhoneIntegrations,
  ])

  return { options, isLoading }
}
